// Authentication utilities
import { API_BASE_URL, API_ENDPOINTS } from './config';
import { isMockInterviewPath } from './mockInterviewEntry';
import { makeTraceHeaders } from './traceId';
import { getCookie, setCookie } from '@/lib/analytics/browser';
import {
  ANONYMOUS_ID_COOKIE,
  ANONYMOUS_ID_MAX_AGE_SECONDS,
} from '@/lib/analytics/identity';

export const AUTH_CHANGE_EVENT = 'auth-change';

const TOKEN_KEY = 'authToken';

/**
 * Store authentication token in localStorage
 */
export const setAuthToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }
};

/**
 * Scheduled interview: exchange calendar interview token for a JWT (same response as login).
 * Uses POST JSON to avoid HTTP Basic edge cases that can yield 401 before app logic runs
 */
export async function exchangeInterviewScheduleTokenForSession(token: string): Promise<boolean> {
  const trimmed = token.trim();
  if (!trimmed || !API_BASE_URL) {
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SESSION_FROM_SCHEDULE_TOKEN}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: trimmed }),
    });
    if (!res.ok) {
      return false;
    }
    const data = (await res.json()) as { userData?: { apiToken?: string } };
    const apiToken = data?.userData?.apiToken;
    if (typeof apiToken === 'string' && apiToken.length > 0) {
      setAuthToken(apiToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Get authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(TOKEN_KEY);
  }
  return null;
};

/**
 * Remove authentication token from localStorage and clear sessionStorage so
 * interview/session-scoped data does not persist after logout or token invalidation.
 */
export const removeAuthToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    try {
      sessionStorage.clear();
    } catch {
      // ignore (e.g. storage disabled)
    }
    // Drop in-memory owner gate so the next session does not inherit isOwner
    void import('@/hooks/useEnterpriseOwnerAfterMount')
      .then((m) => m.clearEnterpriseOwnerSessionCache())
      .catch(() => { });
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

/**
 * Decode JWT token to get user information
 */
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    // Error decoding token
    return null;
  }
};

/** JWT roles that represent the enterprise organization owner (legacy + current). */
export const isEnterpriseOwnerJwtRole = (role: string | undefined | null): boolean => {
  return role === 'enterprise' || role === 'enterprise_owner';
};

/** True when JWT was issued by enterprise login/register (organization owner). */
export const isEnterpriseOwnerSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeToken(token) as { role?: string } | null;
  return isEnterpriseOwnerJwtRole(payload?.role);
};

/** True when current persona is enterprise recruiter or owner. */
export const isEnterpriseIdentitySession = (): boolean => {
  if (typeof window === 'undefined') return false;
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeToken(token) as { role?: string } | null;
  const role = payload?.role;
  return isEnterpriseOwnerJwtRole(role) || role === "recruiter";
};

/** True when current session belongs to an enterprise candidate (identity_type === 'enterprise_candidate'). */
export const isEnterpriseCandidateSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeToken(token) as { identity_type?: string } | null;
  return payload?.identity_type === 'enterprise_candidate';
};

/** True when current persona is a personal user (non-enterprise, non-candidate). */
export const isNormalUserSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeToken(token) as { role?: string; identity_type?: string } | null;
  if (!payload) return false;

  // Candidate tokens are not normal users
  if (payload.identity_type === 'enterprise_candidate') return false;

  const role = payload.role;
  // If role is explicitly user or missing (legacy), it's a normal user
  return !role || role === 'user';
};

/**
 * Get current user ID from token
 * After Step 2.6.1.4: JWT token contains UUID string user_id.
 * Returns UUID string from JWT token.
 */
export const getUserId = (): string | null => {
  const token = getAuthToken();
  if (!token) return null;

  const decoded = decodeToken(token);
  const userId = decoded?.user_id;

  // After Step 2.6.1.4: user_id is UUID string in JWT
  // Handle both UUID string (new) and integer (legacy) for backward compatibility
  if (userId === null || userId === undefined) {
    return null;
  }

  // Convert to string (handles both UUID string and integer)
  return String(userId);
};

/**
 * Get current user ID as string from token
 * Alias for getUserId() - kept for backward compatibility.
 * After Step 2.6.1.4: getUserId() already returns string, so this is just an alias.
 */
export const getUserIdAsString = (): string | null => {
  return getUserId();
};

/**
 * Make an authenticated API call with Authorization header
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();

  // Check if the body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;

  // Convert headers to a plain object for easier manipulation
  const headersObj: Record<string, string> = {};

  // If headers is a Headers object, convert it to a plain object
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      // Handle array of tuples format
      options.headers.forEach(([key, value]) => {
        headersObj[key] = value;
      });
    } else {
      // Already a plain object
      Object.assign(headersObj, options.headers);
    }
  }

  // Only set Content-Type to application/json if not FormData
  if (!isFormData && !headersObj['Content-Type']) {
    headersObj['Content-Type'] = 'application/json';
  }

  // Add trace ID so backend services can correlate logs for this request.
  Object.assign(headersObj, makeTraceHeaders());

  // Add Authorization header if token exists
  if (token) {
    headersObj['Authorization'] = `Bearer ${token}`;
  }

  const headers: HeadersInit = headersObj;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized, clear storage and redirect. Consumer /signin is disabled (sends users to /).
  if (response.status === 401) {
    clearAllStorageAndCache().then(() => redirectToSigninAfterAuthFailure());
  }

  // Domain ownership was claimed by another user — this session is now invalid.
  // Clone the response before reading so the caller can still consume the original body.
  if (response.status === 403) {
    try {
      const cloned = response.clone();
      const data = await cloned.json().catch(() => null);
      const detail: string = typeof data?.detail === "string" ? data.detail : "";
      if (detail.includes("already been registered by an enterprise administrator")) {
        clearAllStorageAndCache().then(() => {
          if (typeof window !== "undefined") {
            window.location.replace("/enterprise/sign?reason=domain_claimed");
          }
        });
      } else if (detail.toLowerCase().includes("deactivated") || detail.toLowerCase().includes("removed")) {
        clearAllStorageAndCache().then(() => redirectToSigninAfterAuthFailure());
      }
    } catch {
      // Not a JSON body or parse failed — let caller handle normally
    }
  }

  return response;
};

/**
 * After 401 / session invalidation (storage already cleared in authenticatedFetch).
 * /signin is disabled and sends users to / — avoid that for routes that gate with LoginModal.
 */
export function redirectToSigninAfterAuthFailure(): void {
  if (typeof window === 'undefined') return;
  const p = window.location.pathname;
  if (isMockInterviewPath(p)) {
    window.location.replace(window.location.pathname + window.location.search);
    return;
  }
  if (p === '/practice-questions' || p.startsWith('/practice-questions/')) {
    window.location.replace(window.location.pathname + window.location.search);
    return;
  }
  const enterprisePath = p === '/enterprise' || p.startsWith('/enterprise/');
  // Consumer /signin is disabled (redirects to /); send guests to landing login modal.
  window.location.replace(enterprisePath ? '/enterprise/' : '/');
}

/**
 * Verify token with backend API (uses GET /users/current-user).
 * Returns valid: true if status 200, valid: false if 401 or other error.
 */
export const verifyToken = async (): Promise<{ valid: boolean }> => {
  const token = getAuthToken();

  if (!token) {
    return { valid: false };
  }

  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CURRENT_USER}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      return { valid: true };
    }
    if (response.status === 401) {
      return { valid: false };
    }
    return { valid: false };
  } catch (error) {
    // Error verifying token
    return { valid: false };
  }
};

/**
 * Best-effort removal of cookies visible to `document.cookie` (non-HttpOnly).
 * HttpOnly cookies cannot be cleared from JavaScript.
 */
export function clearAllClientAccessibleCookies(): void {
  if (typeof document === 'undefined') return;
  const cookieString = document.cookie;
  if (!cookieString) return;
  const names = [
    ...new Set(
      cookieString.split(';').map((part) => part.split('=')[0]?.trim()).filter((n): n is string => Boolean(n))
    ),
  ];
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  for (const name of names) {
    const expired = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${expired};path=/`;
    if (host && host !== 'localhost' && host !== '127.0.0.1' && host !== '[::1]') {
      document.cookie = `${expired};path=/;domain=${host}`;
      document.cookie = `${expired};path=/;domain=.${host}`;
    }
  }
}

/**
 * Wipe localStorage, sessionStorage, and client-readable cookies.
 * Used when enterprise (recruiter pipeline) candidates exit an interview and should leave no session footprint in the browser.
 * The long-lived MeritLens analytics anonymousId cookie is restored so browser identity survives logout.
 */
export async function clearBrowserStorageAndCookies(): Promise<void> {
  const anonymousId = getCookie(ANONYMOUS_ID_COOKIE);
  clearAllClientAccessibleCookies();
  if (typeof window !== 'undefined') {
    localStorage.clear();
    sessionStorage.clear();
  }
  if (anonymousId) {
    setCookie(ANONYMOUS_ID_COOKIE, anonymousId, ANONYMOUS_ID_MAX_AGE_SECONDS);
  }
}

/**
 * Logout enterprise candidate and redirect to landing page.
 * Clears all browser storage (localStorage, sessionStorage, cookies) and redirects to /.
 * This ensures enterprise candidates leave no session footprint in the browser.
 */
export const logoutEnterpriseCandidate = async (): Promise<void> => {
  await clearBrowserStorageAndCookies();
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};

/**
 * Clear all storage and cache
 */
export const clearAllStorageAndCache = async (): Promise<void> => {
  try {
    try {
      const { clearEnterpriseOwnerSessionCache } = await import(
        '@/hooks/useEnterpriseOwnerAfterMount'
      );
      clearEnterpriseOwnerSessionCache();
    } catch {
      // ignore — hook module may be unavailable in some bundles
    }

    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.clear();
    }

    // Clear sessionStorage
    if (typeof window !== 'undefined') {
      sessionStorage.clear();
    }

    // Clear Cache API
    try {
      const { clearAllAudioCache } = await import('./audioCache');
      await clearAllAudioCache();
    } catch (cacheError) {
      // Failed to clear Cache API
    }

    // Clear legacy IndexedDB audio-cache database
    if (typeof window !== 'undefined' && 'indexedDB' in window) {
      try {
        const dbRequest = indexedDB.deleteDatabase('audio-cache');
        await new Promise<void>((resolve, reject) => {
          dbRequest.onsuccess = () => {
            resolve();
          };
          dbRequest.onerror = () => {
            resolve(); // Don't reject, just continue
          };
          dbRequest.onblocked = () => {
            resolve(); // Don't reject, just continue
          };
        });
      } catch (dbError) {
        // Don't throw - continue with cleanup
      }
    }
  } catch (error) {
    throw error;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }
};


